import React from 'react';
import { Link } from 'react-router-dom';
import { DocPage, Note, Steps, WqTable, Figure } from '../../components/documentation/web-integration';
import PL1 from '../../assets/docs/create-payment-link-button.png';
import PL2 from '../../assets/docs/choose-link-type.png';
import PL3 from '../../assets/docs/one-time-link.png';
import PL4 from '../../assets/docs/subscription-link.png';

const CONTENTS = ['Payment links', 'Choose a link type', 'One-time payment link', 'Recurrent payment link', 'After you share a link'];

const OPEN_LINKS = [
    <>
        Log in to your merchant dashboard at{' '}
        <a href="https://app.wayaquick.com" target="_blank" rel="noreferrer" className="wq-link">
            app.wayaquick.com
        </a>
        .
    </>,
    <>
        Click <strong>Payment Links</strong> in the side menu.
    </>,
    <>
        Click <strong>Create link</strong>.
    </>,
];

const ONE_TIME_STEPS = [
    <>
        Click <strong>Create link</strong> and choose <strong>One-time payment link</strong>.
    </>,
    <>
        Fill in the details: a name and description your customer will recognise, and the amount.
    </>,
    <>
        <strong>Set the amount:</strong> Enter an amount to charge everyone the same fixed price.
    </>,
    <>
        Optionally, click <strong>Customise your payment link</strong> to add more options, such as a page to send
        customers to after they pay.
    </>,
    <>
        Save. WayaQuick creates your link — <strong>copy it and share it</strong> with your customers.
    </>,
];

const RECURRENT_STEPS = [
    <>
        Click <strong>Create link</strong> and choose <strong>Subscription payment link</strong>.
    </>,
    <>Fill in the details and the amount to charge each time.</>,
    <>
        <strong>Choose the interval</strong> — how often to charge the customer, for example weekly or monthly.
    </>,
    <>
        <strong>Set how many times to charge</strong> before the subscription stops by itself. If you leave this
        empty, the customer is charged every interval until you or the customer cancels.
    </>,
    <>
        Optionally, click <strong>Customise your payment link</strong> for more options, then save, copy and share
        your link.
    </>,
];

const AFTER_SHARING = [
    <>
        <strong>Your customer opens the link</strong> and lands on the secure WayaQuick checkout.
    </>,
    <>
        <strong>They pay</strong> with any payment method enabled on your account: dynamic virtual account, bank
        transfer, card, USSD or PayAttitude.
    </>,
    <>
        <strong>WayaQuick confirms the payment</strong> and it appears in your dashboard under Transactions.
    </>,
    <>
        <strong>You get paid.</strong> The money is settled to your default settlement account (Settings ›
        WayaQuick Settings › Settlement Account).
    </>,
];

function PaymentLink() {
    return (
        <DocPage
            seo={{
                title: 'Payment Links',
                description:
                    'Create one-time and recurrent WayaQuick payment links to collect payments without a website or app.',
            }}
            eyebrow="Collect payments · No code"
            title={
                <>
                    WayaQuick — <em>Payment Links</em>
                </>
            }
            contents={CONTENTS}
            footer="WayaQuick · Payment Links · Create, share and track links from your merchant dashboard."
            // prev={{ to: '/merchant-dashboard', label: 'WayaQuick Merchant Dashboard' }}
            prev={{ to: '/payment', label: 'Payments' }}
            next={{ to: '/dev-get-started', label: 'Getting Started' }}
        >
            <section className="wq-section" id={CONTENTS[0]}>
                <h2 className="wq-h2">Payment links</h2>
                <p className="wq-lead">
                    A payment link is the quickest way to get paid. Create one in your merchant dashboard and share it
                    by WhatsApp, SMS, email or social media — no website, app or code needed. Your customer opens the
                    link and pays on the secure WayaQuick checkout.
                </p>

                <h4 className="wq-h4">Open payment links</h4>
                <Steps items={OPEN_LINKS} />
                <Figure src={PL1} alt="Payment links page in the WayaQuick dashboard" />

                <Note label="Before you start">
                    You can create links in Test mode to see how they work, but test links don't take real payments.
                    To collect real money, your KYC must be approved and your dashboard switched to Live mode —{' '}
                    <Link to="/test-environment" className="wq-link">
                        see Going live
                    </Link>
                    .
                </Note>
            </section>

            <section className="wq-section" id={CONTENTS[1]}>
                <h2 className="wq-h2">Choose a link type</h2>
                <p className="wq-p">There are two kinds of payment link. Pick the one that matches how you charge.</p>
                <WqTable
                    columns={['Link type', 'Charges the customer', 'Good for']}
                    rows={[
                        [
                            <strong>One-time</strong>,
                            'Once, each time someone pays',
                            'Product sales, event tickets, donations, invoices',
                        ],
                        [
                            <strong>Recurrent</strong>,
                            'Repeatedly, on a schedule you set',
                            'Subscriptions, memberships, instalment plans',
                        ],
                    ]}
                />
                <Figure src={PL2} alt="Choosing between a one-time and a recurrent payment link" />
            </section>

            <section className="wq-section" id={CONTENTS[2]}>
                <h2 className="wq-h2">One-time payment link</h2>
                <p className="wq-p">Charges the customer once. The same link can be paid by as many customers as you like.</p>
                <Figure src={PL3} alt="One-time payment link form" />
                <Steps items={ONE_TIME_STEPS} />
                <Note label="Exact amounts only">
                    Customers must pay the exact amount on the link. If someone pays more or less, the payment is
                    rejected automatically, and any money that came through is reversed to them.
                </Note>
            </section>

            <section className="wq-section" id={CONTENTS[3]}>
                <h2 className="wq-h2">Recurrent payment link</h2>
                <p className="wq-p">
                    Also called a subscription link. It charges the customer on a schedule — for example, every month.
                </p>
                <Figure src={PL4} alt="Recurrent payment link form" />
                <Steps items={RECURRENT_STEPS} />

                <h4 className="wq-h4">Collecting in instalments</h4>
                <p className="wq-p">
                    WayaQuick doesn't accept part payments, so use a recurrent link to split a large amount. Divide the
                    total by the number of payments yourself and use the result as the link amount. For example, to
                    collect ₦120,000 over 4 months, set the amount to <strong>₦30,000</strong>, the interval to{' '}
                    <strong>monthly</strong> and the number of charges to <strong>4</strong>.
                </p>
            </section>

            <section className="wq-section" id={CONTENTS[4]}>
                <h2 className="wq-h2">After you share a link</h2>
                <Steps items={AFTER_SHARING} />
                <p className="wq-p">
                    Want to create payment links from your own system instead of the dashboard? The{' '}
                    <Link to="/sdks?type=server" className="wq-link">
                        SDKs
                    </Link>{' '}
                    can create them for you.
                </p>
            </section>
        </DocPage>
    );
}

export default PaymentLink;